import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  FileText,
  Filter,
  CalendarDays,
  ExternalLink,
  Pencil,
  Loader2,
  Search,
} from "lucide-react";

import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { listAllProposalsAdmin } from "@/lib/api/proposals";
import { listSimulators } from "@/lib/api/simulators";
import { ViewerGuard } from "@/lib/auth/AdminGuard";
import { UserFilter } from "@/components/dashboard/UserFilter";
import type { ProposalAdmin } from "@/types";

export const Route = createFileRoute("/_dashboard/admin/proposals")({
  component: AdminProposalsPage,
});

function formatDateParis(dateStr: string): string {
  let d: Date;
  if (!dateStr.endsWith("Z") && !dateStr.includes("+")) {
    d = new Date(dateStr + "Z");
  } else {
    d = new Date(dateStr);
  }
  return new Intl.DateTimeFormat("fr-FR", {
    timeZone: "Europe/Paris",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

function AdminProposalsPage() {
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const [simulatorFilter, setSimulatorFilter] = useState<string>("all");
  const [userFilter, setUserFilter] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");
  const [search, setSearch] = useState<string>("");

  useEffect(() => {
    const handle = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data: simulatorsData } = useQuery({
    queryKey: ["simulators-list"],
    queryFn: () =>
      listSimulators({ page_size: 100, include_unpublished: true }),
  });

  const { data, isLoading } = useQuery({
    queryKey: [
      "admin-proposals",
      page,
      pageSize,
      simulatorFilter,
      userFilter,
      dateFrom,
      dateTo,
      search,
    ],
    queryFn: () =>
      listAllProposalsAdmin({
        page,
        page_size: pageSize,
        simulator_id:
          simulatorFilter !== "all"
            ? parseInt(simulatorFilter)
            : undefined,
        user_id:
          userFilter !== "all" ? parseInt(userFilter) : undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        search: search || undefined,
      }),
  });

  const resetFilters = () => {
    setSimulatorFilter("all");
    setUserFilter("all");
    setDateFrom("");
    setDateTo("");
    setSearchInput("");
    setSearch("");
    setPage(1);
  };

  const hasActiveFilters =
    simulatorFilter !== "all" ||
    userFilter !== "all" ||
    dateFrom !== "" ||
    dateTo !== "" ||
    search !== "";

  const handleOpenCotation = (proposal: ProposalAdmin) => {
    if (!proposal.simulator_id || !proposal.simulation_id) return;
    const url = `/simulators/${proposal.simulator_id}/simulate?loadSimulation=${proposal.simulation_id}&t=${Date.now()}`;
    window.open(url, "_blank");
  };

  return (
    <ViewerGuard>
      <div className="flex flex-col h-full">
        <Header title="Toutes les propositions commerciales" />

        <div className="flex-1 p-6 space-y-6 overflow-auto">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Filter className="h-4 w-4" />
                  <span className="text-sm font-medium">Filtres</span>
                </div>

                <div className="relative w-[260px]">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    type="search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Coclico ou raison sociale…"
                    className="h-9 pl-8 text-sm"
                  />
                </div>

                <Select
                  value={simulatorFilter}
                  onValueChange={(v) => {
                    setSimulatorFilter(v);
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Simulateur" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      Tous les simulateurs
                    </SelectItem>
                    {simulatorsData?.items.map((sim) => (
                      <SelectItem
                        key={sim.id}
                        value={String(sim.id)}
                      >
                        {sim.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <UserFilter
                  value={userFilter}
                  onChange={(v) => {
                    setUserFilter(v);
                    setPage(1);
                  }}
                />

                <div className="flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="prop-date-from" className="sr-only">
                      Du
                    </Label>
                    <Input
                      id="prop-date-from"
                      type="date"
                      value={dateFrom}
                      onChange={(e) => {
                        setDateFrom(e.target.value);
                        setPage(1);
                      }}
                      className="h-9 w-[140px] text-sm"
                    />
                    <span className="text-xs text-muted-foreground">
                      –
                    </span>
                    <Label htmlFor="prop-date-to" className="sr-only">
                      Au
                    </Label>
                    <Input
                      id="prop-date-to"
                      type="date"
                      value={dateTo}
                      onChange={(e) => {
                        setDateTo(e.target.value);
                        setPage(1);
                      }}
                      className="h-9 w-[140px] text-sm"
                    />
                  </div>
                </div>

                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetFilters}
                  >
                    Réinitialiser
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="whitespace-nowrap">
                        Date
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        Nom
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        Raison sociale
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        Coclico
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        Utilisateur
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        Simulateur
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        Réf. cotation
                      </TableHead>
                      <TableHead className="whitespace-nowrap">
                        Versions
                      </TableHead>
                      <TableHead className="whitespace-nowrap text-right">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoading ? (
                      [...Array(5)].map((_, i) => (
                        <TableRow key={i}>
                          {[...Array(9)].map((_, j) => (
                            <TableCell key={j}>
                              <Skeleton className="h-4 w-20" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : data?.items.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={9}
                          className="h-32 text-center"
                        >
                          <div className="flex flex-col items-center gap-2 text-muted-foreground">
                            <FileText className="h-10 w-10 opacity-50" />
                            <p>
                              Aucune proposition commerciale trouvée
                            </p>
                            {hasActiveFilters && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={resetFilters}
                              >
                                Réinitialiser les filtres
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.items.map((proposal, index) => {
                        return (
                          <TableRow
                            key={proposal.id}
                            className="border-b transition-colors hover:bg-muted/50"
                            style={{
                              animation: `fadeInUp 0.3s ease-out ${index * 0.03}s both`,
                            }}
                          >
                            <TableCell className="text-sm whitespace-nowrap">
                              {formatDateParis(proposal.created_at)}
                            </TableCell>

                            <TableCell className="text-sm">
                              <span className="truncate max-w-[200px] block font-medium">
                                {proposal.name}
                              </span>
                            </TableCell>

                            <TableCell className="text-sm">
                              <span
                                className="truncate max-w-[180px] block"
                                title={
                                  proposal.raison_sociale || undefined
                                }
                              >
                                {proposal.raison_sociale || "—"}
                              </span>
                            </TableCell>

                            <TableCell className="text-sm">
                              <span
                                className="font-mono text-xs truncate max-w-[140px] block"
                                title={proposal.coclico || undefined}
                              >
                                {proposal.coclico || "—"}
                              </span>
                            </TableCell>

                            <TableCell className="text-sm">
                              <div className="flex flex-col">
                                <span
                                  className="truncate max-w-[200px] block"
                                  title={
                                    proposal.user_email ||
                                    undefined
                                  }
                                >
                                  {proposal.user_full_name ||
                                    proposal.user_email ||
                                    "—"}
                                </span>
                                {proposal.user_full_name &&
                                  proposal.user_email && (
                                    <span className="text-xs text-muted-foreground truncate max-w-[200px] block">
                                      {proposal.user_email}
                                    </span>
                                  )}
                              </div>
                            </TableCell>

                            <TableCell className="text-sm">
                              <span className="truncate max-w-[180px] block">
                                {proposal.simulator_name || "—"}
                              </span>
                            </TableCell>

                            <TableCell className="text-sm">
                              <span
                                className="font-mono text-xs truncate max-w-[200px] block"
                                title={
                                  proposal.simulation_ref ||
                                  undefined
                                }
                              >
                                {proposal.simulation_ref || "—"}
                              </span>
                            </TableCell>

                            <TableCell className="text-sm text-center">
                              {proposal.version_count}
                            </TableCell>

                            <TableCell>
                              <div className="flex items-center justify-end gap-1">
                                <Link
                                  to="/proposals/$id"
                                  params={{
                                    id: String(proposal.id),
                                  }}
                                  target="_blank"
                                >
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    title="Ouvrir la proposition"
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                </Link>
                                {proposal.simulator_id && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() =>
                                      handleOpenCotation(proposal)
                                    }
                                    title="Ouvrir la cotation"
                                  >
                                    <ExternalLink className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Pagination */}
          {data && data.total_pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                {data.total_items} proposition
                {data.total_items > 1 ? "s" : ""} au total
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setPage((p) => Math.max(1, p - 1))
                  }
                  disabled={page === 1}
                >
                  Précédent
                </Button>
                <div className="flex items-center gap-1 px-3 py-1 bg-muted rounded text-sm">
                  {page} / {data.total_pages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setPage((p) =>
                      Math.min(data.total_pages, p + 1)
                    )
                  }
                  disabled={page === data.total_pages}
                >
                  Suivant
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </ViewerGuard>
  );
}
